import * as yup from 'yup';
import { toDateInputValue } from '../../../library/dateTimeHelper';
import {
    DOCUMENT_TITLE_MAX,
    MAX_DOCUMENTS_PER_UPLOAD,
    MEDICAL_DOCUMENT_CATEGORIES,
    MedicalDocumentCategory,
} from '../../../library/models/medicalRecord';
import { requiredText, TranslateFn } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * What the upload form holds while it is being filled in.
 *
 * One category and one date for the whole batch, and a title per file. A batch is one
 * visit's worth of paper -- the three pages of one report, photographed -- so the kind and
 * the date are asked once, and the title is the one thing that tells the files apart.
 *
 * The files themselves are not here. A `File` is not a form value: react-hook-form would
 * have to carry it through validation and reset, and Yup has nothing useful to say about
 * one. The dialog keeps them in its own state, in step with `documents` by position.
 */
export interface UploadDocumentsFormValues {
    /** Empty until one is chosen: there is no sensible default kind of document. */
    category: MedicalDocumentCategory | '';
    /** ISO date. The date on the document, not today's. */
    issuedOn: string;
    documents: { title: string }[];
}

/**
 * Deliberately a restatement of what the API checks, so that the person is answered before
 * they have waited for twenty megabytes to upload: a kind, a date that is not in the future,
 * between one and ten files, a title on each.
 *
 * `today` is a parameter so the rule can be tested against a fixed day. ISO dates compare
 * correctly as strings, which is all "not in the future" needs.
 */
export const createUploadDocumentsSchema = (
    translate: TranslateFn,
    today: string = toDateInputValue(new Date())
) =>
    yup.object({
        category: yup
            .string()
            .required(translate(ResourcesIds.UPLOAD_DOCUMENTS_CATEGORY_REQUIRED))
            .oneOf(MEDICAL_DOCUMENT_CATEGORIES, translate(ResourcesIds.UPLOAD_DOCUMENTS_CATEGORY_REQUIRED)),
        issuedOn: yup
            .string()
            .required(translate(ResourcesIds.UPLOAD_DOCUMENTS_ISSUED_ON_REQUIRED))
            .test(
                'is-not-in-the-future',
                translate(ResourcesIds.UPLOAD_DOCUMENTS_ISSUED_ON_FUTURE),
                (value?: string) => !value || value <= today
            ),
        documents: yup
            .array()
            .of(
                yup.object({
                    title: requiredText(
                        translate, ResourcesIds.UPLOAD_DOCUMENTS_TITLE_REQUIRED, DOCUMENT_TITLE_MAX),
                })
            )
            .required()
            .min(1, translate(ResourcesIds.UPLOAD_DOCUMENTS_NO_FILES))
            .max(
                MAX_DOCUMENTS_PER_UPLOAD,
                translate(ResourcesIds.UPLOAD_DOCUMENTS_TOO_MANY, { max: MAX_DOCUMENTS_PER_UPLOAD })
            ),
    });
